// client/src/pages/ManageAuction.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import logo from '../assets/chitconnect-logo-blue.png'; // <- ensure this file exists
import '../App.css';

export default function ManageAuction() {
  const { groupId } = useParams();

  const [period, setPeriod] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [showEvidence, setShowEvidence] = useState(false);

  // One-time load for current/last period + bids
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      setMessage('');
  setFinalizeResult(null);
  setShowEvidence(false);
      try {
        // This endpoint returns { period, bids } (bids sorted desc on server)
        const res = await apiFetch(`/api/groups/auction/${groupId}/current`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (mounted) {
            setPeriod(null);
            setBids([]);
            setError(data?.error || 'No current period found');
          }
        } else {
          if (mounted) {
            setPeriod(Number(data?.period) || null);
            setBids(Array.isArray(data?.bids) ? data.bids : []);
          }
        }
      } catch {
        if (mounted) setError('Server error while fetching auction data.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [groupId]);

 const handleFinalize = async () => {
  setError('');
  setMessage('');
  try {
    // 1) Ask backend for bidding status so we can pick the right period
    const sRes = await apiFetch(`/api/bidding/status/${groupId}`);
    const status = await sRes.json().catch(() => ({}));

    // Try to derive “last ended & not finalised” from whatever shape we get
    const pickFromArray = (arr) => {
      // Prefer objects that look like: { period_number, status: 'ended', finalized_at|null, winner_id|null }
      const ended = arr.filter(p =>
        String(p.status || '').toLowerCase() === 'ended' &&
        (p.finalized_at == null && p.winner_id == null && p.winner == null)
      );
      if (ended.length) return Math.max(...ended.map(p => Number(p.period_number || p.period || 0)));
      // Fallback: highest period with no winner marker
      const noWin = arr.filter(p => (p.winner_id == null && p.winner == null && p.is_winner == null));
      if (noWin.length) return Math.max(...noWin.map(p => Number(p.period_number || p.period || 0)));
      return null;
    };

    let periodToFinalize = null;
    if (Array.isArray(status?.periods)) {
      periodToFinalize = pickFromArray(status.periods);
    } else if (Array.isArray(status?.history)) {
      periodToFinalize = pickFromArray(status.history);
    } else if (typeof status?.last_unfinalized_period === 'number') {
      periodToFinalize = status.last_unfinalized_period;
    }

    // As a last resort: if the UI currently shows “#4”, try “#3”
    if (!periodToFinalize && typeof period === 'number' && period > 1) {
      periodToFinalize = period - 1;
    }

    if (!periodToFinalize) {
      setError('Nothing to finalise right now.');
      return;
    }

    // 2) Finalise that period explicitly (force=1 lets organiser close immediately)
    const res = await apiFetch(`/api/bidding/finalize/${groupId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ period: periodToFinalize, force: 1 }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Finalize failed (HTTP ${res.status})`);

    setFinalizeResult(data || null);
    if (String(data?.method || '').toLowerCase() === 'lucky_dip') setShowEvidence(true);

    setMessage(
      data?.winner_name
        ? `Winner finalised: ${data.winner_name} (${data.method === 'lucky_dip' ? 'Lucky Dip' : 'Bid'})`
        : 'Winner finalised.'
    );

    // 3) Refresh current/last and bids
    const r2 = await apiFetch(`/api/groups/auction/${groupId}/current`);
    const d2 = await r2.json().catch(() => ({}));
    if (r2.ok) {
      setPeriod(Number(d2?.period) || null);
      setBids(Array.isArray(d2?.bids) ? d2.bids : []);
    }
  } catch (e) {
    setError(e.message || 'Server error while finalising.');
  }
};


  const topBid = useMemo(() => (Array.isArray(bids) && bids.length ? bids[0] : null), [bids]);

  return (
    <div className="main-content">
      {/* Header strip with logo — matches the rest of the app */}
      <div style={{ textAlign: 'center', marginBottom: 20, minHeight: 56 }}>
        <img
          src={logo}
          alt="ChitConnect"
          style={{ height: 48, objectFit: 'contain' }}
        />
      </div>

      {message && <div className="toast-message">{message}</div>}
      {error && <p className="error-text">{error}</p>}

      {showEvidence && finalizeResult?.method === 'lucky_dip' && (
        <div className="card" style={{ padding: 16, marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Lucky Dip evidence</div>
          <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 10 }}>
            This period had no bids, so the system selected a winner using public randomness (Drand) when available,
            otherwise a commit/reveal fallback. The proof below is stored in the database as <code>proof_json</code>.
          </div>

          <details open style={{ userSelect: 'text' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700 }}>View proof</summary>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginTop: 10,
                background: '#f7f7f7',
                borderRadius: 12,
                padding: 12,
                overflow: 'auto',
                maxHeight: 240,
              }}
            >
{(() => {
  try {
    return JSON.stringify(JSON.parse(finalizeResult?.proof_json || '{}'), null, 2);
  } catch {
    return String(finalizeResult?.proof_json || '');
  }
})()}
            </pre>
          </details>
        </div>
      )}

      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Auction Management</h2>
          <div style={{ fontWeight: 600 }}>Group #{groupId}</div>
        </div>

        {loading ? (
          <p style={{ marginTop: 12 }}>Loading…</p>
        ) : (
          <>
            <h3 style={{ marginTop: 16, marginBottom: 6 }}>
              Current/last period: {period ? `#${period}` : '—'}
            </h3>
            <p style={{ marginTop: 0, color: '#555', fontSize: 13 }}>
              Tip: This screen may show an upcoming/active period. The <em>Finalise Winner</em> button
              finalises the last-ended unfinalised period automatically.
            </p>

            <div className="table-responsive">
              {Array.isArray(bids) && bids.length ? (
                <table className="group-table" style={{ minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th>Bidder</th>
                      <th>Email</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map((b, i) => (
                      <tr key={i} style={{ background: i === 0 ? '#e0ffe0' : 'transparent' }}>
                        <td>{[b.given_name, b.surname].filter(Boolean).join(' ') || b.full_name || '—'}</td>
                        <td>{b.email || '—'}</td>
                        <td>${Number(b.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No bids yet for this period.</p>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <button className="btn" onClick={handleFinalize}>Finalise Winner</button>
              {topBid && (
                <span style={{ marginLeft: 12, fontSize: 13, color: '#333' }}>
                  Highest bid: <strong>${Number(topBid.amount).toLocaleString()}</strong>
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}